import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { developerApi } from '../../api/developer.api';
import type {
  ApiTokenListItemDto,
  CreateApiTokenRequestDto,
  CreateApiTokenResponseDto,
} from '../../types';

export function useDeveloperTokens() {
  return useQuery<ApiTokenListItemDto[]>({
    queryKey: ['developer', 'tokens'],
    queryFn: async () => {
      const { data } = await developerApi.get('/v1/developer/api-tokens');
      if (Array.isArray(data)) return data;
      if (Array.isArray(data?.items)) return data.items;
      if (Array.isArray(data?.data?.items)) return data.data.items;
      if (Array.isArray(data?.data)) return data.data;
      return [];
    },
    staleTime: 30_000,
  });
}

export function useCreateDeveloperToken() {
  const queryClient = useQueryClient();
  return useMutation<
    CreateApiTokenResponseDto,
    AxiosError<{ message?: string | string[] }>,
    CreateApiTokenRequestDto
  >({
    mutationFn: async ({ label }) => {
      const { data } = await developerApi.post('/v1/developer/api-tokens', { label: label.trim() });
      return data.data || data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['developer', 'tokens'] });
    },
  });
}

export function useRevokeDeveloperToken() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await developerApi.delete(`/v1/developer/api-tokens/${id}`);
      return data.data || data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['developer', 'tokens'] });
    },
  });
}
